import { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ProgressSpinner } from "primereact/progressspinner";
import { Badge, ButtonBox, Skeleton as SkeletonCustom } from "@vm-synthia/sdk-react";
import { linkAuthRoute, linkPageAudioAnalysis } from "routes";
import useAudioWorkflowStatus, { STEP_UI, WORKFLOW_STATUS_UI } from "./useAudioWorkflowStatus";

interface WorkflowStepsViewProps {
  /** Audio url identifying the tracked workflow; feeds the `/audio/progress` long-poll. */
  audioUrl: string;
  /** Optional kickoff message returned by the upload endpoint. */
  processingStartMessage?: string;
  onClose: () => void;
}

/** Label-bar widths for the placeholder step rows shown until the first poll returns. */
const SKELETON_STEP_WIDTHS = ["45%", "55%", "50%", "70%", "80%"];

/**
 * Dialog body showing the per-step status of one audio workflow. Mounting this
 * component starts the 3s `/audio/progress` poll (via useAudioWorkflowStatus);
 * unmounting it — closing the dialog — stops it.
 */
const WorkflowStepsView = ({ audioUrl, processingStartMessage, onClose }: WorkflowStepsViewProps) => {
  const navigate = useNavigate();
  const progressListRef = useRef<HTMLDivElement | null>(null);

  const {
    pipelineProgress,
    completedCount,
    totalCount,
    progressLoading,
    hasLoaded,
    fileId,
    processedUrl,
    status: workflowStatus,
  } = useAudioWorkflowStatus(audioUrl);

  const analysisRedirectQuery = useMemo(() => {
    const params = new URLSearchParams();
    if (fileId) {
      params.set("file-id", fileId);
      // PageAudioAnalysis also reads audioId; keep it aligned with file id for backend lookups.
      params.set("audioId", fileId);
    }
    if (audioUrl) {
      params.set("audio", audioUrl);
    }
    if (processedUrl) {
      params.set("processed-audio", processedUrl);
    }
    // Keep a non-empty document id since analysis endpoint expects this query parameter.
    params.set("documentId", "default");
    return params.toString();
  }, [fileId, audioUrl, processedUrl]);

  const canNavigateToAudioAnalysis = workflowStatus === "succeeded" && !!fileId && !!audioUrl;
  const isRunning = workflowStatus === "running" || workflowStatus === "pending";

  useEffect(() => {
    const el = progressListRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [completedCount, canNavigateToAudioAnalysis]);

  const overallStatus = workflowStatus !== "idle" ? WORKFLOW_STATUS_UI[workflowStatus] : null;

  return (
    <>
      <div className="flex min-h-0 flex-col overflow-auto rounded-lg border border-border-weak bg-surface-primary">
        <div className="flex shrink-0 items-center justify-between border-b border-border-weakest px-3 py-2.5">
          <span className="text-label-medium text-text-secondary">
            Analysis & PD Report Progress{hasLoaded && ` (${completedCount}/${totalCount})`}
          </span>
          <div className="flex items-center gap-2">
            {hasLoaded && isRunning && progressLoading && (
              <ProgressSpinner style={{ width: "14px", height: "14px" }} strokeWidth="8" />
            )}
            {!hasLoaded && <SkeletonCustom width="4.5rem" height="1.25rem" borderRadius="999px" />}
            {hasLoaded && overallStatus && (
              <Badge label={overallStatus.label} variant="tint" state={overallStatus.badgeState} size="Small" />
            )}
          </div>
        </div>
        {processingStartMessage && (
          <div className="shrink-0 border-b border-border-weakest px-3 py-2">
            <div className="rounded-md bg-surface-info-weakest px-3 py-2 text-body-small text-text-info">
              {processingStartMessage}
            </div>
          </div>
        )}
        {workflowStatus === "failed" && (
          <div className="shrink-0 border-b border-border-weakest px-3 py-2">
            <div className="rounded-md bg-surface-error-weakest px-3 py-2 text-body-small text-text-error">
              The workflow stopped because a step failed. Please re-upload the audio to try again.
            </div>
          </div>
        )}
        <div ref={progressListRef} className="max-h-[min(45vh,420px)] flex-1 overflow-auto">
          <div className="min-h-0 px-3 py-1">
            {/* Placeholder rows mirror a real step row's box, so the dialog doesn't grow
                or jump when the first `/audio/progress` response lands. */}
            {!hasLoaded &&
              SKELETON_STEP_WIDTHS.map((width, index) => (
                <div
                  key={`skeleton-${index}`}
                  className="flex items-center gap-3 rounded-md px-2 py-2.5 text-body-small"
                >
                  <SkeletonCustom shape="circle" size="1rem" className="shrink-0" />
                  {/* The zero-width space gives the row the same line height as a text label. */}
                  <span className="flex flex-1 items-center">
                    &#8203;
                    <SkeletonCustom width={width} height="0.75rem" />
                  </span>
                </div>
              ))}
            {hasLoaded && pipelineProgress.map((step) => {
              const ui = STEP_UI[step.state];
              const isActive = step.state === "in_progress";
              return (
                <div
                  key={step.key}
                  className={`flex items-center gap-3 rounded-md px-2 py-2.5 text-body-small ${
                    isActive ? "bg-surface-info-weakest" : ""
                  }`}
                >
                  <i className={`pi ${ui.icon} ${ui.iconClass} w-icon-sm shrink-0 text-center`} />
                  <span className={`flex-1 ${ui.labelClass}`}>{step.label}</span>
                  {ui.caption && (
                    <span className={`shrink-0 text-body-xsmall ${ui.iconClass}`}>{ui.caption}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-end gap-1.5">
        <ButtonBox
          label={isRunning ? "Run in background" : "Close"}
          variant="secondary"
          size="small"
          onClick={onClose}
        />
        {canNavigateToAudioAnalysis && (
          <ButtonBox
            label="Go to Audio Analysis"
            variant="primary"
            size="small"
            onClick={() =>
              navigate(linkAuthRoute + linkPageAudioAnalysis + `?${analysisRedirectQuery}`)
            }
            className="!bg-purple-600 hover:!bg-purple-700"
          />
        )}
      </div>
    </>
  );
};

export default WorkflowStepsView;
